// src/pages/CartPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartItem from '../components/cart/CartItem';
import CartSummary from '../components/cart/CartSummary';
import EmptyCart from '../components/cart/EmptyCart';
import Loading from '../components/common/Loading';
import cartService from '../services/cartService';

/**
 * Cart Page Component
 * 
 * Features:
 * - Display cart items (with custom names)
 * - Update quantity
 * - Remove items
 * - Cart summary with totals
 * - Proceed to checkout
 */
const CartPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { 
    cart, 
    loading, 
    updateCartItem, 
    removeCartItem 
  } = useCart();
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }
  
  // Handle quantity update
  const handleUpdateQuantity = async (itemId, newQuantity) => {
    try {
      await updateCartItem(itemId, newQuantity);
    } catch (error) {
      alert('Failed to update quantity');
    }
  };
  
  // Handle item removal
  const handleRemoveItem = async (itemId) => {
    if (window.confirm('Remove this item from cart?')) {
      try {
        await removeCartItem(itemId);
      } catch (error) {
        alert('Failed to remove item');
      }
    }
  };
  
  // Handle checkout
  const handleCheckout = () => {
    navigate('/checkout');
  };
  
  // Show loading
  if (loading) {
    return <Loading fullScreen message="Loading your cart..." />;
  }
  
  // Show empty cart
  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmptyCart />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
          <p className="text-gray-600 mt-2">
            {cart.totalItems} {cart.totalItems === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>
        
        {/* Cart Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items (Left - 2 columns) */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cart.items.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemoveItem}
                  loading={loading}
                />
              ))}
            </div>
          </div>
          
          {/* Cart Summary (Right - 1 column) */}
          <div className="lg:col-span-1">
            <CartSummary
              cart={cart}
              onCheckout={handleCheckout}
              checkoutLoading={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;